import { useEffect, useState } from "react";
import { ApiError, listAudits, type AuditSummary } from "../api/client";

/**
 * Screen 34 — the lead auditor's own audits.
 *
 * The rows are the same rows a certification body sees, narrowed by the **database** rather
 * than by a second query: what an auditor may see is a tenancy question, and answering it
 * in the browser would put the answer in two places. Drafts never appear — an intake nobody
 * has finished has allocated no auditor, so there is no auditor for it to belong to.
 *
 * **The Action column is the server's answer, not a lookup here.** What this caller has to
 * do next is a grant resolved from role × state × approvals, which is the same kind of
 * answer as what they may read. A browser computing it would be a second copy of the flow's
 * rules that can disagree with the routes enforcing them — and it is why the column already
 * knows about the reporting states the session console has not been built for yet: an audit
 * in `report_drafted` is waiting on the lead auditor, and a list that went quiet after the
 * closing meeting would hide the step that actually holds up certification.
 */
export function MyAudits({ onOpen }: { onOpen: (auditId: string) => void }) {
  const [audits, setAudits] = useState<AuditSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAudits()
      .then(setAudits)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "The audits could not be read."),
      );
  }, []);

  return (
    <>
      <div className="eyebrow">Lead auditor</div>
      <h1>My audits</h1>
      <p className="sub" style={{ marginBottom: "18px" }}>
        Audits allocated to you by your certification body.
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Client organisation</th>
                <th>Audit date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {audits === null ? (
                <tr>
                  <td colSpan={5}>Reading your audits&hellip;</td>
                </tr>
              ) : audits.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    No audit has been allocated to you. Your certification body allocates the
                    lead auditor at intake.
                  </td>
                </tr>
              ) : (
                audits.map((audit) => (
                  <tr
                    key={audit.id}
                    className="rowlink"
                    onClick={() => onOpen(audit.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onOpen(audit.id);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                  >
                    <td>
                      <span className="mono">{audit.reference ?? "—"}</span>
                    </td>
                    <td>
                      <b>{audit.client_organisation}</b>
                      <br />
                      <span className="sub">
                        {(audit.standards ?? []).join(" · ") || "No standards selected"}
                      </span>
                    </td>
                    <td>{audit.planned_date ?? "—"}</td>
                    <td>
                      <Status audit={audit} />
                    </td>
                    <td>
                      <Action action={audit.action} label={audit.action_label} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * The action, in the tone that says whether it is *yours*.
 *
 * Amber is waiting on this auditor, green is running, and plain text is neither. An action
 * nobody has to take must not look like one somebody does — the whole use of the column is
 * telling those two apart at a glance.
 */
function Action({ action, label }: { action: string; label: string }) {
  if (action === "none") return <span className="sub">{label}</span>;
  if (
    action === "oversee_session" ||
    action === "start_session" ||
    // The audit day, with the client's move outstanding. Green because the auditor's
    // oversight begins the moment the session opens and they are expected to be there for
    // it — not amber, which on this column means *you owe something*.
    action === "await_session_start"
  ) {
    return <span className="pill p-ok">{label}</span>;
  }
  if (
    action === "await_documents" ||
    action === "await_plan" ||
    action === "await_audit_day"
  ) {
    return <span className="sub">{label}</span>;
  }
  return <span className="pill p-wait">{label}</span>;
}

/**
 * The Status column — the state, in the words the screen uses for it.
 *
 * **`awaiting_documents` is read against the row's derived facts rather than on its own.** It
 * is where the audit sits from the plan being issued to the client opening the session, and
 * neither the last document arriving nor the planned date coming round writes anything: the
 * next state written is `session_live`, by the auditee. Rendering the column from the state
 * alone told a lead auditor *Awaiting documents* on a morning the client had filed all
 * eighteen and was being offered *Begin audit session* — and if the certification body had
 * moved the audit date, that is precisely the day it said it.
 *
 * Both facts are the server's (`audits.audit_day`, `audits.all_documents_in`), because both
 * are answers a browser would get differently: one is a clock and the other is a checklist
 * resolved against the standards in scope.
 *
 * **The colour comes from the same branch as the label, not from `audit_day` on its own.**
 * `audit_day` is true of every audit whose date has gone, a closed one included, so colouring
 * on it directly turns *Report drafted* and *Closed* green — the tone that means the audit is
 * running.
 */
function Status({ audit }: { audit: AuditSummary }) {
  if (audit.state === "awaiting_documents" || audit.state === "session_scheduled") {
    if (audit.audit_day) return <span className="pill p-ok">Audit day</span>;
    return (
      <span className="pill p-wait">
        {audit.documents_filed ? "Documents filed" : "Awaiting documents"}
      </span>
    );
  }
  return <span className="pill p-wait">{stateLabel(audit.state)}</span>;
}

function stateLabel(state: string): string {
  const words: Record<string, string> = {
    determination_pending: "Awaiting approval",
    plan_generated: "Plan in review",
    session_live: "Audit live",
    reconfirmation: "Re-confirmation required",
  };
  return (
    words[state] ?? state.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}
